import type { OutstandingSessionRow, OutstandingTabDto } from '@gc/shared';

/** One finished, unpaid sitting on a device, as the device's dialog lists it. */
export interface UnpaidPlayRow {
  sessionId: string;
  tabId: string;
  /** The party's name — who to ask for the money. */
  name: string;
  /** `H:MM`, as the Outstanding screen shows it. */
  duration: string;
  endedAt: string | null;
  /** `POWERED_OFF` for a sitting its PC going away stopped — the dialog says "PC turned off". */
  endedReason: OutstandingSessionRow['endedReason'];
  totalMd: string;
}

/**
 * The play a device is still owed for: sittings that ENDED on it and were
 * never paid, read from the Outstanding screen's own list.
 *
 * NOT a bills query (audit, 2026-09-23). The device's "Outstanding bills"
 * dialog asked for OPEN RESERVATION bills, and there are none: a reservation
 * bill only exists once checkout has been paid (`@gc/shared` outstanding.ts
 * says why). Production held 6,196 PAID and 0 OPEN, so a PC whose customer
 * played three hours and walked out read "No open bills on this device" while
 * the money sat on the Outstanding screen under a name. Unpaid time lives on
 * the open tabs as closed sittings with no bill — which is exactly what
 * `GET /tabs/outstanding` serves, already priced the way the till charges.
 *
 * Closed sittings only: the one still running is on the tile itself, with its
 * clock. Newest first, because the last person to leave is the one staff are
 * most likely asking about.
 */
export function unpaidPlayOnDevice(
  tabs: readonly OutstandingTabDto[],
  deviceId: string,
): UnpaidPlayRow[] {
  return tabs
    .flatMap((tab) =>
      tab.sessions
        .filter((session) => session.deviceId === deviceId && session.status === 'CLOSED')
        .map((session) => ({
          sessionId: session.sessionId,
          tabId: tab.tabId,
          name: tab.name,
          duration: session.duration,
          endedAt: session.endedAt,
          endedReason: session.endedReason,
          totalMd: session.totalMd,
        })),
    )
    .sort((a, b) => (b.endedAt ?? '').localeCompare(a.endedAt ?? ''));
}
